import { Button, Col, DatePicker, Input, Modal, Row, Select } from "antd";
import { RangePickerValue } from "antd/lib/date-picker/interface";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import * as GPSInsightsActions from "../../../Actions/GPSInsightsActions";
import { getBrand } from "../../../Actions/GPSInsightsActions";
import { GET_BRAND_SUCCESS } from "../../../Actions/Types";
import { IBrand } from "../../../Models/IBrand";
import { IRootReducer } from "../../../Models/IRootReducer";
import { Loading } from "../../Common/Loading/Loading";
import ItemMap from "./ItemMap";
import {
    BrandCard,
    CloseButton,
    GeographyResult,
    Header,
    Info,
    Item,
    SourceCard,
    Tip,
    Wrapper
} from "./Styled";

interface IGPSInsightsFormProps { }
interface IGPSInsightsFormStore {
    loadingBrand: boolean;
    brand: IBrand | null;
}
enum MobileInsightStep {
    Source,
    Category,
    Brand,
    Geography,
    Time,
    Benchmark,
}
enum Category {
    Retail,
    Restaurant,
    Healthcare,
}
enum GeographyType {
    Blockgroup,
    ZipCode,
    County,
    DMA,
    State,
    National,
}
enum Source {
    Polygons,
    Category,
    Brand,
}

const getLoadingBrand = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.loadingBrand,
    (loadingBrand: boolean) => loadingBrand
);

const getCurrentBrand = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.brand,
    (brand: IBrand) => brand
);

export const GPSInsightsForm = (props: IGPSInsightsFormProps): JSX.Element => {
    /** misc */
    const dispatch = useDispatch();
    /** store */
    const loadingBrand: boolean = useSelector(getLoadingBrand);
    const brand: IBrand = useSelector(getCurrentBrand);
    /** state */
    const [step, setStep] = useState<MobileInsightStep>(MobileInsightStep.Source);
    const [source, setSource] = useState<Source | null>(null);
    const [category, setCategory] = useState<Category | null>(null);
    const [geographyType, setGeographyType]
        = useState<GeographyType | null>(null);
    const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);
    const [brandSearchTerm, setBrandSearchTerm] = useState<string>("");

    const handleCategoryChange = (category: Category): void => {
        setCategory(category);
    };

    const handleSearchBrands = (value: string): void => {
        const trimmedTerm: string = value.trim();
        dispatch(getBrand(trimmedTerm));
        setBrandSearchTerm(trimmedTerm);
    };

    const handleGeographyTypeChange = (type: GeographyType): void => {
        setGeographyType(type);
    };

    const handleGeographyTypeSearch = (value: string): void => { };

    const handleStepChange = (forwards: boolean): void => {
        switch (step) {
            case MobileInsightStep.Source: {
                if (forwards) {
                    setStep(MobileInsightStep.Category);
                }
                break;
            }
            case MobileInsightStep.Category: {
                if (forwards) {
                    setStep(MobileInsightStep.Brand);
                } else {
                    setCategory(null);
                    setStep(MobileInsightStep.Source);
                }
                break;
            }
            case MobileInsightStep.Brand: {
                if (forwards) {
                    setStep(MobileInsightStep.Geography);
                } else {
                    setSelectedBrand(null);
                    /** reset the brand - they need to search again */
                    dispatch({
                        type: GET_BRAND_SUCCESS,
                        data: "",
                    });
                    setStep(MobileInsightStep.Category);
                }
                break;
            }
            case MobileInsightStep.Geography: {
                if (forwards) {
                    setStep(MobileInsightStep.Time);
                } else {
                    setGeographyType(null);
                    setStep(MobileInsightStep.Brand);
                }
                break;
            }
            case MobileInsightStep.Time: {
                if (forwards) {
                    setStep(MobileInsightStep.Benchmark);
                } else {
                    setStep(MobileInsightStep.Geography);
                }
                break;
            }
            case MobileInsightStep.Benchmark: {
                if (!forwards) {
                    setStep(MobileInsightStep.Time);
                }
                break;
            }
        }
    };

    const handleGeographyResultRemove = (): void => { };

    const handleSelectSource = (source: Source): void => {
        setSource(source);
    };

    const getNextDisabledState = (): boolean => {
        let disabled = false;
        switch (step) {
            case MobileInsightStep.Source: {
                disabled = source === null;
                break;
            }
            case MobileInsightStep.Category: {
                disabled = category === null;
                break;
            }
            case MobileInsightStep.Brand: {
                disabled = selectedBrand === null;
                break;
            }
            case MobileInsightStep.Geography: {
                disabled = geographyType === null;
                break;
            }
            case MobileInsightStep.Time: {
                break;
            }
            case MobileInsightStep.Benchmark: {
                break;
            }
        }
        return disabled;
    };

    const handleTimeRangeChange = (e: RangePickerValue): void => {
        console.log(e);
    };

    const handleBrandSelect = (b: IBrand): void => {
        setSelectedBrand(b);
    };

    const closeModal = (): void => {
        dispatch(GPSInsightsActions.setGpsInsightModalVisible(false));
    };

    return (
        <Modal
            title="Create a New Mobile Insight"
            centered={true}
            visible={true}
            style={{
                minWidth: "50%",
            }}
            onCancel={closeModal}
            footer={[
                step !== MobileInsightStep.Source && (
                    <Button
                        key={0}
                        loading={false}
                        disabled={false}
                        onClick={() => handleStepChange(false)}
                    >
                        Back
                    </Button>
                ),
                <Button
                    key={1}
                    loading={false}
                    disabled={getNextDisabledState()}
                    onClick={() => handleStepChange(true)}
                >
                    Next
                </Button>,
            ]}
        >
            {step === MobileInsightStep.Source && (
                <>
                    <h4>HOW WOULD YOU LIKE TO START</h4>
                    <h5>
                        Select from your custom polygons or start from a standard geography
                    </h5>
                    <Row gutter={16}>
                        <Col span={8}>
                            <SourceCard
                                hoverable={true}
                                onClick={() => handleSelectSource(Source.Polygons)}
                                className={`${source === Source.Polygons && "selected"}`}
                            >
                                <img src="" />
                                My Saved Polygons
                            </SourceCard>
                        </Col>
                        <Col span={8}>
                            <SourceCard
                                hoverable={true}
                                onClick={() => handleSelectSource(Source.Category)}
                                className={`${source === Source.Category && "selected"}`}
                            >
                                <img src="" />
                                Choose Category
                            </SourceCard>
                        </Col>
                        <Col span={8}>
                            <SourceCard
                                hoverable={true}
                                onClick={() => handleSelectSource(Source.Brand)}
                                className={`${source === Source.Brand && "selected"}`}
                            >
                                <img src="" />
                                Choose Brand
                            </SourceCard>
                        </Col>
                    </Row>
                </>
            )}
            {step === MobileInsightStep.Category && (
                <>
                    <h4>SELECT A CATEGORY</h4>
                    <h5>Select either Retail, Restaurant, or Healthcare</h5>
                    <Select
                        onChange={handleCategoryChange}
                        defaultValue={category}
                        style={{ width: "100%" }}
                    >
                        <Select.Option value={Category.Retail}>Retail</Select.Option>
                        <Select.Option value={Category.Restaurant}>
                            Restaurant
                        </Select.Option>
                        <Select.Option value={Category.Healthcare}>
                            Healthcare
                        </Select.Option>
                    </Select>
                    <Tip>
                        <p>
                            <b>Tip</b>
                        </p>
                        <p>
                            The category is based off of machine learning forensics, machine
                            unlearning learnings, and also some random Udemy course that was
                            purchased at full price. Good luck, choose wisely - or if you
                            don't have time, just pick one.
                        </p>
                    </Tip>
                </>
            )}
            {step === MobileInsightStep.Brand && (
                <>
                    <h4>SELECT A BRAND</h4>
                    <h5>Search for a Brand</h5>
                    <Input.Search
                        placeholder="Search..."
                        enterButton={
                            <Button
                                disabled={loadingBrand || brandSearchTerm.trim().length === 0}
                            >
                                Search
                            </Button>
                        }
                        disabled={loadingBrand}
                        loading={loadingBrand}
                        onChange={(e) => setBrandSearchTerm(e.target.value.trim())}
                        onSearch={(value) => handleSearchBrands(value)}
                    />
                    {loadingBrand && (
                        <Loading title="Loading Brands matching your term" />
                    )}
                    {!loadingBrand && brand && (
                        <BrandCard
                            key={brand.brandId}
                            title={brand.brandName}
                            hoverable={true}
                            className={brand.brandId === selectedBrand?.brandId && "selected"}
                            onClick={() => handleBrandSelect(brand)}
                        />
                    )}
                    <Tip>
                        <p>
                            <b>Tip</b>
                        </p>
                        <p>
                            Brand searching is simply searching for a Brand. Now that you have
                            this powerful information, search.
                        </p>
                    </Tip>
                </>
            )}
            {step === MobileInsightStep.Geography && (
                <>
                    <h4>SELECT A GEOGRAPHY</h4>
                    <h5>Select from a Common Geography</h5>
                    <div style={{ display: "flex" }}>
                        <Select
                            placeholder={"Select a geography..."}
                            onChange={handleGeographyTypeChange}
                            defaultValue={geographyType}
                            style={{
                                margin: "0 1rem 0 0",
                                width: "50%",
                            }}
                        >
                            <Select.Option value={GeographyType.Blockgroup}>
                                Blockgroup
                            </Select.Option>
                            <Select.Option value={GeographyType.ZipCode}>
                                Zip Code
                            </Select.Option>
                            <Select.Option value={GeographyType.County}>County</Select.Option>
                            <Select.Option value={GeographyType.DMA}>DMA</Select.Option>
                            <Select.Option value={GeographyType.State}>State</Select.Option>
                            <Select.Option value={GeographyType.National}>
                                National
                            </Select.Option>
                        </Select>
                        <Input.Search
                            style={{ width: "50%" }}
                            placeholder="Search..."
                            onChange={(e) => handleGeographyTypeSearch(e.target.value)}
                        />
                    </div>
                    <Wrapper>
                        <ItemMap
                            containerElement={<div style={{ width: "100%" }} />}
                            mapElement={<div style={{ height: "100%" }} />}
                        />
                        <GeographyResult>
                            <Header>3 Locations Found</Header>
                            <Item>
                                <Info>
                                    <div>
                                        <b>McDonalds</b>
                                    </div>
                                    <div>Fort Worth, TX. 76111</div>
                                </Info>
                                <CloseButton onClick={() => handleGeographyResultRemove()}>
                                    X
                                </CloseButton>
                            </Item>
                            <Item>
                                <Info>
                                    <div>
                                        <b>Taco Bell</b>
                                    </div>
                                    <div>North Richland Hills, TX. 76123</div>
                                </Info>
                                <CloseButton onClick={() => handleGeographyResultRemove()}>
                                    X
                                </CloseButton>
                            </Item>
                            <Item>
                                <Info>
                                    <div>
                                        <b>Grandys</b>
                                    </div>
                                    <div>Dangersville, TN. 64287</div>
                                </Info>
                                <CloseButton onClick={() => handleGeographyResultRemove()}>
                                    X
                                </CloseButton>
                            </Item>
                        </GeographyResult>
                    </Wrapper>
                </>
            )}
            {step === MobileInsightStep.Time && (
                <>
                    <h4>Choose a range of time</h4>
                    <h5>Narrow your results</h5>
                    <DatePicker.RangePicker
                        format={"MM/DD/YYYY"}
                        onChange={(e) => handleTimeRangeChange(e)}
                    />
                </>
            )}
            {step === MobileInsightStep.Benchmark && (
                <>
                    <h4>Add a Benchmark</h4>
                    <h5>Benchmarking adding....here</h5>
                    <p>Content here</p>
                </>
            )}
        </Modal>
    );
};

function getCategoryTestData() {
    return [
        {
            name: "QSR",
            id: 1,
        },
        {
            name: "Fast Casual",
            id: 2,
        },
        {
            name: "Fine Dining",
            id: 3,
        },
        {
            name: "Unfine Dining",
            id: 4,
        },
    ];
}
